import { Injectable, Optional } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { NgxI18nRootService } from '@studiohyperdrive/ngx-i18n';
import { StoreService, dispatchDataToStore } from '@studiohyperdrive/ngx-store';
import { BooleanSignal } from '@studiohyperdrive/ngx-utils';
import { ObservableArray, ObservableBoolean } from '@studiohyperdrive/rxjs-utils';
import { Observable, map, of } from 'rxjs';

import { MandateEntity } from '@vlaio/mandates/shared';
import { BrowserService } from '@vlaio/shared/core';
import { GTagService } from '@vlaio/shared/gtag';
import {
	CallToActionEnum,
	CallToActionOriginEnum,
	CallToActionGTATypeEnum,
	ProductEntity,
	RequestSuccessResultEntity
} from '@vlaio/shared/types';
import { UserService } from '@vlaio/shared/user';

import { actions, ApplicationStoreSliceType, selectors } from '../application.store';
import { ApplicationRequestEntity } from '../interfaces';

import { ApplicationApiService } from './application.api.service';

@Injectable({ providedIn: 'root' })
export class ApplicationService extends StoreService<ApplicationStoreSliceType> {
	/**
	 * Whether an error occurred while requesting the mandate.
	 */
	public readonly requestError$: ObservableBoolean = this.state.requestResult$.pipe(
		map((result: RequestSuccessResultEntity) => !result.succes)
	);
	/**
	 * The errors that occurred when trying to make a application
	 */
	public readonly errors$: ObservableArray<string> = this.state.requestResult$.pipe(
		map((result: RequestSuccessResultEntity) => result.fouten)
	);

	public readonly isExecutor: BooleanSignal = toSignal(this.userService.isExecutor$);

	constructor(
		public readonly store: Store,
		// Iben: This is an optional dependency because the ApplicationService itself is an optional dependency as well
		@Optional() private readonly apiService: ApplicationApiService,
		private readonly browserService: BrowserService,
		private readonly router: Router,
		private readonly userService: UserService,
		private readonly i18nRootService: NgxI18nRootService,
		private readonly gtagService: GTagService
	) {
		super(store, selectors);
	}
	/**
	 * Set the selected product in the store.
	 *
	 * @param product The product entity to save to the store.
	 */
	public setSelectedProduct(product: ProductEntity): void {
		if (!Object.keys(product).length) {
			return;
		}

		this.store.dispatch(actions.product.set({ payload: product }));
	}

	/**
	 * Whether the handling request should be active (the loading state).
	 *
	 * @param loading The loading state of the request.
	 */
	public setHandlingRequest(loading: boolean): void {
		this.store.dispatch(actions.handlingRequest.set({ payload: loading }));
	}

	/**
	 * Create a new mandate based off the application.
	 *
	 * @param request The required data to make the mandate-creation call.
	 */
	public createNewApplication(request: ApplicationRequestEntity): Observable<any> {
		return dispatchDataToStore(actions.requestResult, this.apiService.createNewApplication(request), this.store);
	}

	/**
	 * Save the configuration of the mandate to be requested.
	 *
	 * @param request The mandate config to be stored.
	 * @returns The stored mandate config.
	 */
	public setMandateToRequest(request: Partial<MandateEntity>): Observable<Partial<MandateEntity>> {
		return dispatchDataToStore(actions.mandateToRequest, of(request), this.store);
	}

	/**
	 * Track the click event of the product CTA. This makes it possible to track
	 * which product was requested access to, and thus which product users are more
	 * interested in.
	 *
	 * @param product The name of the product that was clicked.
	 * @param action The action of the button that was selected.
	 * @param origin The origin of the click event. This can be either the offers page or the dashboard.
	 */
	public trackProductCTAClicked(
		product: string,
		action: CallToActionEnum,
		origin: keyof typeof CallToActionOriginEnum
	): void {
		// Wouter: Track CTA clicked
		this.gtagService.trackProduct(
			CallToActionGTATypeEnum[action],
			'maatregelen',
			CallToActionOriginEnum[origin],
			product
		);
	}
}
